import React, { useEffect, useState } from 'react';
import { usePayerWizardContext } from '../PayerWizardContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, ArrowRight, FileText, Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import { RULESET_SOURCES, type RuleReviewSummary } from '@/lib/payer-wizard/types';
import { AUTH_REQUIRED_COLORS, AUTH_REQUIRED_LABELS, type AuthRequired } from '@/lib/rules/rulesetTypes';

export function PayerStep3RuleReview() {
  const { 
    progress, 
    ruleReview,
    validateStep3, 
    completeStep, 
    goToStep,
    fetchTemplateRules,
    confirmRulesReviewed,
  } = usePayerWizardContext();
  
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  
  useEffect(() => {
    if (!progress) return;
    
    const loadRules = async () => {
      setLoading(true);
      for (const plan of progress.plans) {
        if (plan.ruleset_source !== 'EXISTING_PLAN') {
          await fetchTemplateRules(plan.ruleset_source);
        }
      }
      setLoading(false);
    };
    
    loadRules();
  }, [progress, fetchTemplateRules]);
  
  if (!progress) return null;
  
  const validation = validateStep3();
  
  const handleConfirm = (checked: boolean) => {
    setConfirmed(checked);
    if (checked) {
      confirmRulesReviewed();
    }
  };
  
  const handleContinue = () => {
    if (validation.isValid) {
      completeStep(3);
    }
  };
  
  const getRuleSummary = (source: string): RuleReviewSummary | null => {
    return ruleReview[source] || null;
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Step 3: Rule Review
        </CardTitle>
        <CardDescription>
          Review the default rules that will be applied to each plan. You can modify these after activation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {progress.plans.map((plan, index) => {
              const summary = getRuleSummary(plan.ruleset_source);
              
              return (
                <div key={index} className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">{plan.plan_name}</h3>
                    <Badge variant="outline">
                      {RULESET_SOURCES.find(s => s.value === plan.ruleset_source)?.label}
                    </Badge>
                  </div>
                  
                  {summary ? (
                    <div className="grid grid-cols-2 gap-4">
                      {/* Auth Requirement */}
                      <div className="space-y-1">
                        <Label className="text-muted-foreground text-xs">Authorization Requirement</Label>
                        <Badge 
                          variant="outline"
                          className={AUTH_REQUIRED_COLORS[summary.auth_requirement_default as AuthRequired] || ''}
                        >
                          {AUTH_REQUIRED_LABELS[summary.auth_requirement_default as AuthRequired] || summary.auth_requirement_default}
                        </Badge>
                      </div>
                      
                      {/* SLA */}
                      <div className="space-y-1">
                        <Label className="text-muted-foreground text-xs">SLA Timer</Label>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>{summary.sla_hours} hours ({Math.round(summary.sla_hours / 24)} days)</span>
                        </div>
                      </div>
                      
                      {/* Follow-up */}
                      <div className="space-y-1">
                        <Label className="text-muted-foreground text-xs">Follow-up Cadence</Label>
                        <span>Every {summary.follow_up_cadence_hours} hours</span>
                      </div>
                      
                      {/* Rule Count */}
                      <div className="space-y-1">
                        <Label className="text-muted-foreground text-xs">Total Rules</Label>
                        <span>{summary.rule_count} rules</span>
                      </div>
                      
                      {/* Required Documents */}
                      <div className="col-span-2 space-y-1">
                        <Label className="text-muted-foreground text-xs">Default Required Documents</Label>
                        <div className="flex flex-wrap gap-1">
                          {summary.doc_requirements.map((doc, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {doc}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <AlertTriangle className="h-4 w-4" />
                      <span>Rule preview not available for cloned rulesets</span>
                    </div>
                  )}
                </div>
              );
            })}
            
            <Separator />
            
            {/* Confirmation Checkbox */}
            <div className="flex items-start gap-3 p-4 border rounded-lg bg-muted/30">
              <Checkbox
                id="confirm_rules"
                checked={confirmed}
                onCheckedChange={handleConfirm}
              />
              <div className="space-y-1">
                <Label htmlFor="confirm_rules" className="font-medium cursor-pointer">
                  I have reviewed the rule configuration
                </Label>
                <p className="text-sm text-muted-foreground">
                  By checking this box, you confirm that you have reviewed the authorization requirements, 
                  documentation checklists, SLA timers, and follow-up cadence for all plans.
                </p>
              </div>
            </div>
          </>
        )}
        
        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => goToStep(2)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button onClick={handleContinue} disabled={!confirmed || loading}>
            Continue to Lane Configuration
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
